import { api } from "./base_service";
import { mapEntityType, normalizeDob, normalizeInputDob } from "./screening_service";
import { normalizeDelimitedList, joinCountryNames, stringifyAdditional } from "./normalize_helpers";
import { apiTokenForSelection } from "@/components/watchlists";
import { ADVERSE_MEDIA_KEYWORD_LABELS } from "./search_configurations_service";
import type { EntityType } from "@/components/filter-cards";
import type {
  AlternativeName,
  Match,
  MatchDecision,
  MatchDecisionStatus,
  RiskLevel,
  ListingIdentifier,
  Position,
} from "@/app/(app)/screening/_data/types";
import { isoCountryName } from "@/app/(app)/_lib/iso-countries";
import type { ApiError, Paginated, Result } from "./types";
import { apiEndpoints } from "@/src/config/api/endpoints";

export type { EntityType };

export type MonitoringEntry = {
  id: string;
  name: string;
  /** Passport / national ID. */
  identifier: string;
  /** Customer's own reference, e.g. CUST-10421. */
  externalId?: string;
  /** ISO alpha-2; the first of `countries`. */
  country: string;
  countries?: string[];
  /** Uppercased watchlist tokens, e.g. "UK-SANCTIONS", "ADV-MEDIA". */
  dataSource?: string[];
  dob: string | null;
  type: EntityType | undefined;
  dateAdded: string;
  matches: number;
  /** False positives (dismissed matches). */
  fp: number;
  /** Real positives (confirmed matches). */
  rp: number;
  newCount: number;
  /** "PROCESSING" until the first screen finishes, then "PROCESSED". */
  status: string;
  /** 0–1. Only on the detail endpoint, not the list. */
  minScore?: number;
};

export type AddMonitoringInput = {
  name: string;
  externalIdentifier?: string;
  identifier?: string;
  dob?: string | null;
  countries?: string[];
  entityType?: EntityType;
  /** Empty = all watchlists. */
  watchlists?: string[];
  /** Integer percentage (80–99); defaults to 85. */
  confidenceScore?: number;
};

export type MonitoringSummary = {
  total: number;
  matched: number;
  /** 0–100. */
  matchRate: number;
};

// ---- raw API shapes ----

export type EntityRaw = {
  id: string;
  name: string;
  identifier: string[];
  external_identifier: string;
  /** Can be partial ("1952-00-00", "1952-10"), year-only or array-wrapped — use normalizeInputDob. */
  date_of_birth: string | string[];
  /** Array, CSV string, or CSV inside an array — use normalizeDelimitedList. */
  country: string | string[] | null;
  /** Detail endpoint only. Same shapes as `country`. */
  data_source?: string | string[] | null;
  entity_type: string;
  /** When the name was added. */
  timestamp: string;
  false_positives: number;
  real_positives: number;
  new_matches: number;
  status: string;
  is_local?: boolean;
  min_score?: number;
};

/** Only id, status and text are confirmed; everything else is read defensively. */
type ResultRaw = Record<string, unknown>;

const MON = { monitoring: true } as const;
const DEFAULT_MIN_SCORE = 0.85;

const ENTITY_TYPE_TO_API: Record<string, string> = {
  Individual: "Individual",
  Organization: "Entity",
  Vessel: "Vessel",
  Aircraft: "Aircraft",
  Country: "Country",
};
const ENTRIES_PAGE_SIZE = 100;

// ---- adapters ----

function asString(v: unknown): string {
  if (typeof v === "string") return v;
  if (v == null) return "";
  return String(v);
}

export function adaptEntity(raw: EntityRaw): MonitoringEntry {
  const fp = raw.false_positives ?? 0;
  const rp = raw.real_positives ?? 0;
  const newCount = raw.new_matches ?? 0;
  const countries = normalizeDelimitedList(raw.country);
  return {
    id: asString(raw.id),
    name: raw.name ?? "",
    identifier: (Array.isArray(raw.identifier) ? raw.identifier[0] : raw.identifier) || "",
    externalId: raw.external_identifier || "",
    country: countries[0] ?? "",
    countries,
    dataSource: normalizeDelimitedList(raw.data_source).map((s) => s.toUpperCase()),
    dob: normalizeInputDob(raw.date_of_birth),
    type: raw.entity_type ? mapEntityType(raw.entity_type) : undefined,
    dateAdded: asString(raw.timestamp),
    matches: fp + rp + newCount,
    fp,
    rp,
    newCount,
    status: asString(raw.status),
    minScore: typeof raw.min_score === "number" ? raw.min_score : undefined,
  };
}

function riskFromScore(score: number): RiskLevel {
  if (score >= 0.9) return "critical";
  if (score >= 0.8) return "high";
  if (score >= 0.6) return "medium";
  return "low";
}

function decisionFromResult(raw: ResultRaw): MatchDecision | undefined {
  const status = asString(raw.status);
  let mappedStatus: MatchDecision["status"];
  switch (status) {
    case "False Positive":
      mappedStatus = "false-positive";
      break;
    case "Real Positive":
      mappedStatus = "real-positive";
      break;
    case "New":
      mappedStatus = "new-result";
      break;
    default:
      return undefined;
  }
  return {
    status: mappedStatus,
    comment: asString(raw.text),
    decidedAt: asString(raw.updated_at ?? raw.decided_at ?? raw.modified_at).slice(0, 10),
    decidedBy: asString(raw.reviewed_by ?? raw.decided_by),
  };
}

function adaptResult(raw: ResultRaw): Match {
  // The match itself is nested under data_entry; older shapes are flat.
  const de: ResultRaw =
    raw.data_entry && typeof raw.data_entry === "object" ? (raw.data_entry as ResultRaw) : raw;
  // Not raw.min_score: that's the search threshold, not this match's confidence.
  const scoreRaw = raw.confidence_score ?? raw.score ?? raw.match_score ?? de.score;
  const scoreNum = typeof scoreRaw === "number" ? scoreRaw : Number(scoreRaw);
  const score = Number.isFinite(scoreNum) ? (scoreNum > 1 ? scoreNum / 100 : scoreNum) : 0;
  const source = asString(
    de.data_source ?? raw.data_source ?? raw.source ?? raw.dataset,
  ).toUpperCase();
  // The sanctions-list identifier — not raw.id, which is our internal row id.
  const siIdentifier = asString(de.si_identifier ?? raw.si_identifier) || undefined;
  const sourceListUrl = asString(de.source_list_url ?? raw.source_list_url) || undefined;
  const sourceInformationUrl =
    asString(de.source_information_url ?? raw.source_information_url) || undefined;
  const identifiersRaw = Array.isArray(de.identifiers)
    ? de.identifiers
    : Array.isArray(raw.identifiers)
      ? raw.identifiers
      : [];
  const identifiers: ListingIdentifier[] = identifiersRaw
    .filter((i): i is Record<string, unknown> => Boolean(i) && typeof i === "object")
    .map((i) => ({
      identifier: asString(i.identifier),
      type: asString(i.type),
      country: asString(i.country) || undefined,
      notes: asString(i.notes) || undefined,
    }))
    .filter((i) => i.identifier);
  const positionsRaw = Array.isArray(de.position)
    ? de.position
    : Array.isArray(raw.position)
      ? raw.position
      : [];
  const positions: Position[] = positionsRaw
    .filter((p): p is Record<string, unknown> => Boolean(p) && typeof p === "object")
    .map((p) => ({
      title: asString(p.title),
      organization: asString(p.organization) || undefined,
      country: asString(p.country) || undefined,
      startDate: asString(p.start_date) || undefined,
      endDate: asString(p.end_date) || undefined,
      notes: asString(p.notes) || undefined,
    }))
    .filter((p) => p.title);

  if (source === "ADV-MEDIA") {
    const countryIso = asString(de.country ?? raw.country);
    const country = countryIso ? isoCountryName(countryIso) || countryIso : "";
    const fullTitle = asString(de.title ?? raw.title ?? raw.name);
    const additionalInformation: Record<string, string> = {};
    const url = asString(de.url ?? raw.url);
    const ms =
      de.media_source && typeof de.media_source === "object"
        ? (de.media_source as Record<string, unknown>)
        : null;
    const mediaSourceName = asString(ms?.name ?? de.media_source_name ?? raw.media_source_name);
    const mediaSourceUrl = asString(ms?.url ?? de.media_source_url ?? raw.media_source_url);
    const keywords = Array.isArray(de.keywords)
      ? de.keywords.filter((k): k is string => typeof k === "string")
      : [];
    const individuals = Array.isArray(de.individuals)
      ? de.individuals.filter((i): i is string => typeof i === "string")
      : [];
    const entities = Array.isArray(de.entities)
      ? de.entities.filter((e): e is string => typeof e === "string")
      : [];
    const language = asString(de.language ?? raw.language);
    const imageArr = Array.isArray(de.image) ? de.image : [];
    const image = imageArr.length > 0 ? asString(imageArr[0]) : "";
    const description = asString(de.description ?? raw.description);
    const body = asString(de.body ?? raw.body);
    if (fullTitle.length > 95) additionalInformation["Full Title"] = fullTitle;
    if (url) additionalInformation["Article URL"] = url;
    if (mediaSourceName) additionalInformation["Media Source"] = mediaSourceName;
    if (mediaSourceUrl) additionalInformation["Source URL"] = mediaSourceUrl;
    if (keywords.length) {
      additionalInformation["Keywords"] = keywords
        .map((k) => ADVERSE_MEDIA_KEYWORD_LABELS[k] ?? k)
        .join(", ");
    }
    if (individuals.length) additionalInformation["Individuals"] = individuals.join(", ");
    if (entities.length) additionalInformation["Entities"] = entities.join(", ");
    if (language) additionalInformation["Language"] = language;
    if (image) additionalInformation["Image"] = image;
    return {
      id: asString(raw.id ?? raw.result_id),
      siIdentifier,
      score,
      name: fullTitle.length > 95 ? `${fullTitle.slice(0, 95)}...` : fullTitle,
      type: "Media",
      country,
      countryIso: countryIso || undefined,
      dob: null,
      source: "ADV-MEDIA",
      collection: "adverse_media",
      firstImported: asString(de.first_import ?? raw.first_import).slice(0, 10) || undefined,
      lastUpdated: asString(
        de.published_at ?? raw.published_at ?? raw.last_updated ?? de.last_updated,
      ).slice(0, 10),
      mediaSource: mediaSourceName || undefined,
      riskLevel: riskFromScore(score),
      alternativeNames: [],
      knownLocations: country ? [{ label: "Country", value: country }] : [],
      remarks: description ? [description] : [],
      additionalInformation,
      articleBody: body || undefined,
      decision: decisionFromResult(raw),
    };
  }

  // Same priority as screening_service's adaptMatch — keep both in sync.
  const countryResidence = normalizeDelimitedList(de.country_residence ?? raw.country_residence);
  const nationality = normalizeDelimitedList(de.nationality ?? raw.nationality);
  const citizenship = normalizeDelimitedList(de.citizenship ?? raw.citizenship);
  const legacyCountry = normalizeDelimitedList(
    de.country ?? de.countries ?? raw.country ?? raw.countries,
  );
  const country = joinCountryNames([
    ...countryResidence,
    ...nationality,
    ...citizenship,
    ...legacyCountry,
  ]);
  const countryIso = nationality[0] ?? countryResidence[0] ?? citizenship[0] ?? legacyCountry[0];
  const altNamesRaw = Array.isArray(de.alt_names)
    ? de.alt_names
    : Array.isArray(raw.alt_names)
      ? raw.alt_names
      : [];
  const alternativeNames: AlternativeName[] = altNamesRaw
    .filter((n): n is string => typeof n === "string")
    .map((name) => ({ name, kind: "alias" }));
  // Not raw.text: that's the reviewer's triage note (decision.comment).
  const remarksText = asString(de.remarks);
  return {
    id: asString(raw.id ?? raw.result_id),
    siIdentifier,
    score,
    name: asString(de.name ?? de.title ?? raw.name ?? raw.matched_name ?? raw.caption),
    type: (() => {
      const t = asString(de.entity_type ?? raw.entity_type);
      return t ? mapEntityType(t) : "Individual";
    })(),
    country,
    countryIso: countryIso || undefined,
    dob: normalizeDob((de.date_of_birth ?? raw.date_of_birth) as string | string[] | undefined),
    source,
    sourceListUrl,
    sourceInformationUrl,
    collection: "sanctions_lists",
    firstImported: asString(de.first_import ?? raw.first_import).slice(0, 10) || undefined,
    lastUpdated: asString(raw.last_updated ?? raw.timestamp ?? de.last_updated).slice(0, 10),
    riskLevel: riskFromScore(score),
    alternativeNames,
    knownLocations: country ? [{ label: "Country of listing", value: country }] : [],
    identifiers,
    positions,
    remarks: remarksText ? [remarksText] : [],
    additionalInformation: stringifyAdditional(
      (de.additional_information ?? raw.additional_information) as
        | Record<string, unknown>
        | undefined,
    ),
    decision: decisionFromResult(raw),
  };
}

function resultRows(data: { results?: ResultRaw[] } | ResultRaw[] | undefined): ResultRaw[] {
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.results) ? data.results : [];
}

// ---- summary ----

export function summarizeEntries(entries: MonitoringEntry[]): MonitoringSummary {
  const total = entries.length;
  const matched = entries.filter((e) => e.matches > 0).length;
  const matchRate = total === 0 ? 0 : (matched / total) * 100;
  return { total, matched, matchRate };
}

// ---- entry operations ----

export type ListEntriesResult = {
  entries: MonitoringEntry[];
  /** Total for this search/filter across all pages. */
  count: number;
};

export async function listEntries(opts?: {
  search?: string;
  filter?: string;
  page?: number;
  pageSize?: number;
}): Promise<Result<ListEntriesResult>> {
  try {
    const params: Record<string, unknown> = {
      page_size: opts?.pageSize ?? ENTRIES_PAGE_SIZE,
      page: opts?.page ?? 1,
      ...FILTER_TO_PARAMS[opts?.filter ?? ""],
    };
    if (opts?.search) params.search = opts.search;
    const data = await api.get<Paginated<EntityRaw>>(apiEndpoints.monitoring().entries().list(), {
      ...MON,
      params,
    });
    return {
      ok: true,
      value: {
        entries: Array.isArray(data?.results) ? data.results.map(adaptEntity) : [],
        count: typeof data?.count === "number" ? data.count : 0,
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

const OPEN_ALERTS_PAGE_SIZE = 10;

export async function listOpenAlerts(page = 1): Promise<Result<ListEntriesResult>> {
  try {
    const data = await api.get<Paginated<EntityRaw>>(apiEndpoints.monitoring().entries().list(), {
      ...MON,
      params: { alerts: "true", page_size: OPEN_ALERTS_PAGE_SIZE, page },
    });
    return {
      ok: true,
      value: {
        entries: Array.isArray(data?.results) ? data.results.map(adaptEntity) : [],
        count: typeof data?.count === "number" ? data.count : 0,
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export type NewMatchesPreview = {
  count: number;
};

export async function getNewMatchesPreview(): Promise<Result<NewMatchesPreview>> {
  try {
    const page = await api.get<Paginated<EntityRaw>>(apiEndpoints.monitoring().entries().list(), {
      ...MON,
      params: { new_matches: "true", page_size: 1, page: 1 },
    });
    return { ok: true, value: { count: typeof page?.count === "number" ? page.count : 0 } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export type MonitoringKpi = {
  total: number;
  matched: number;
  /** 0–100. */
  matchRate: number;
  lastAlertAt: string | null;
};

type MonitoringKpiRaw = {
  monitoring_entities_count?: number;
  matched_monitoring_entities?: number;
  match_rate?: number | string;
  last_alert_at?: string | null;
};

export async function getMonitoringKpi(): Promise<Result<MonitoringKpi>> {
  try {
    const data = await api.get<MonitoringKpiRaw>(apiEndpoints.monitoring().statistics(), MON);
    const matchRateNum =
      typeof data?.match_rate === "number" ? data.match_rate : Number(data?.match_rate);
    return {
      ok: true,
      value: {
        total:
          typeof data?.monitoring_entities_count === "number" ? data.monitoring_entities_count : 0,
        matched:
          typeof data?.matched_monitoring_entities === "number"
            ? data.matched_monitoring_entities
            : 0,
        matchRate: Number.isFinite(matchRateNum) ? matchRateNum : 0,
        lastAlertAt: typeof data?.last_alert_at === "string" ? data.last_alert_at : null,
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/** Company-wide, unlike listRecentMonitoringBatches. */
export async function hasEntriesStillProcessing(): Promise<Result<boolean>> {
  try {
    const data = await api.get<Paginated<EntityRaw>>(apiEndpoints.monitoring().entries().list(), {
      ...MON,
      params: { status: "PROCESSING", page_size: 1 },
    });
    return { ok: true, value: typeof data?.count === "number" && data.count > 0 };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

type MonitoringBatchRaw = {
  id?: string;
  timestamp?: string | null;
  pre_processed?: boolean;
};

export type MonitoringBatchSummary = {
  id: string;
  uploadedAt: string | null;
  /** All rows have their entry created. */
  preProcessed: boolean;
};

function adaptBatch(raw: MonitoringBatchRaw): MonitoringBatchSummary {
  return {
    id: asString(raw.id),
    uploadedAt: raw.timestamp || null,
    preProcessed: raw.pre_processed === true,
  };
}

/** The signed-in user's own batches only (not the company's), newest first, one page. */
export async function listRecentMonitoringBatches(): Promise<Result<MonitoringBatchSummary[]>> {
  try {
    const data = await api.get<Paginated<MonitoringBatchRaw>>(
      apiEndpoints.monitoring().batches().list(),
      MON,
    );
    const rows = Array.isArray(data?.results) ? data.results : [];
    return { ok: true, value: rows.filter((raw) => !!raw.id).map(adaptBatch) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function addMonitoring(input: AddMonitoringInput): Promise<Result<MonitoringEntry>> {
  try {
    const minScore =
      input.confidenceScore != null ? input.confidenceScore / 100 : DEFAULT_MIN_SCORE;
    const body = new URLSearchParams({
      external_identifier: input.externalIdentifier?.trim() ?? "",
      name: input.name,
      identifier: input.identifier?.trim() ?? "",
      date_of_birth: input.dob ?? "",
      country: (input.countries ?? []).join(","),
      // Empty = no type filter (both Individual and Organization).
      entity_type: input.entityType
        ? (ENTITY_TYPE_TO_API[input.entityType] ?? input.entityType)
        : "",
      data_source: (input.watchlists ?? []).map(apiTokenForSelection).filter(Boolean).join(","),
      min_score: String(minScore),
    }).toString();

    const raw = await api.post<EntityRaw>(apiEndpoints.monitoring().entries().create(), body, {
      ...MON,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    return { ok: true, value: adaptEntity(raw) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/** Entries are created asynchronously, so `added` is best-effort. */
export async function uploadBatch(file: File): Promise<Result<{ added: number }>> {
  try {
    const form = new FormData();
    form.append("monitoring_batch_file", file);
    const data = await api.post<{ added?: number; count?: number }>(
      apiEndpoints.monitoring().uploadBatch(),
      form,
      MON,
    );
    const added = typeof data?.added === "number" ? data.added : (data?.count ?? 0);
    return { ok: true, value: { added } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

// ---- match-level operations (entry detail) ----

const ENTRY_RESULTS_PAGE_SIZE = 100;

export type EntryResultsKind = "watchlists" | "adverse_media";

export type EntryResultsPage = { matches: Match[]; count: number; next: string | null };

export async function listEntryResults(
  entryId: string,
  kind: EntryResultsKind,
  page = 1,
): Promise<Result<EntryResultsPage>> {
  try {
    const data = await api.get<Paginated<ResultRaw>>(
      apiEndpoints.monitoring().matches().results(entryId),
      {
        ...MON,
        params: {
          page_size: ENTRY_RESULTS_PAGE_SIZE,
          page,
          ...(kind === "adverse_media"
            ? { adverse_media_results: "true" }
            : { data_entry_results: "true" }),
        },
      },
    );
    return {
      ok: true,
      value: {
        matches: resultRows(data).map(adaptResult),
        count: typeof data?.count === "number" ? data.count : 0,
        next: data?.next ?? null,
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function getEntryDetail(entryId: string): Promise<Result<{ entry: MonitoringEntry }>> {
  try {
    const data = await api.get<EntityRaw>(apiEndpoints.monitoring().matches().list(entryId), MON);
    return { ok: true, value: { entry: adaptEntity(data) } };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

/** `_entryId` is unused: result ids are global. */
export async function updateMatchDecision(
  _entryId: string,
  matchId: string,
  decision: MatchDecision,
): Promise<Result<void>> {
  try {
    await api.patch<unknown>(
      apiEndpoints.monitoring().matches().update(matchId),
      { status: statusFromDecision(decision), text: decision.comment ?? "" },
      MON,
    );
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

function statusToApiValue(status: MatchDecisionStatus): string {
  if (status === "false-positive") return "1";
  if (status === "real-positive") return "2";
  return "0";
}

function statusFromDecision(decision: MatchDecision): string {
  return statusToApiValue(decision.status);
}

export async function bulkUpdateMatchDecisions(
  ids: string[],
  status: MatchDecisionStatus,
  text?: string,
): Promise<Result<Match[]>> {
  try {
    const raw = await api.patch<ResultRaw[]>(
      apiEndpoints.monitoring().bulkUpdateStatus(),
      { ids, status: statusToApiValue(status), ...(text ? { text } : {}) },
      MON,
    );
    return { ok: true, value: Array.isArray(raw) ? raw.map(adaptResult) : [] };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function exportEntryReport(entryId: string): Promise<Result<void>> {
  try {
    await api.post<void>(apiEndpoints.monitoring().matches().report(entryId), undefined, MON);
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

// ---- export ----

const FILTER_TO_PARAMS: Record<string, Record<string, string>> = {
  "All Matches": { alerts: "true" },
  "Real matches": { real_positives: "true" },
  "New matches": { new_matches: "true" },
  "False matches": { false_positives: "true" },
};

/** Queued on the backend; the user gets the report by email + notification. */
export async function exportReport(
  filter: string,
  search?: string,
): Promise<Result<{ message: string }>> {
  try {
    const params: Record<string, string> = {
      ...FILTER_TO_PARAMS[filter],
    };
    if (search) params.search = search;
    const data = await api.get<{ message: string }>(apiEndpoints.monitoring().export(), {
      ...MON,
      params,
    });
    return { ok: true, value: data };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

// ---- delete operations ----

export async function bulkDeleteMonitoringEntries(ids: string[]): Promise<Result<void>> {
  try {
    await api.delete<void>(apiEndpoints.monitoring().bulkDelete(), {
      ...MON,
      data: { ids },
      skipAuthRedirect: true,
    });
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

// ---- statistics ----

/** `label` is YYYY-MM-DD (period=month) or YYYY-MM (period=year). */
export interface MonitoringStatPoint {
  label: string;
  count: number;
}

type StatPointRaw = { date?: string; month?: string; count?: number | string };
type StatisticsRaw = { period?: string; data?: StatPointRaw[] };

function statRows(data: StatisticsRaw | StatPointRaw[] | undefined): StatPointRaw[] {
  if (Array.isArray(data)) return data;
  return Array.isArray(data?.data) ? data.data : [];
}

function adaptStatPoint(raw: StatPointRaw): MonitoringStatPoint {
  const count = typeof raw.count === "number" ? raw.count : Number(raw.count);
  return {
    label: asString(raw.date ?? raw.month),
    count: Number.isFinite(count) ? count : 0,
  };
}

export async function getMonitoringStatistics(
  period: "month" | "year",
): Promise<Result<MonitoringStatPoint[]>> {
  try {
    const data = await api.get<StatisticsRaw>(apiEndpoints.monitoring().statisticsWithPeriod(), {
      ...MON,
      params: { period },
    });
    return { ok: true, value: statRows(data).map(adaptStatPoint) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export interface MonitoringTrend {
  month: MonitoringStatPoint[];
  year: MonitoringStatPoint[];
}

export async function getMonitoringTrend(): Promise<MonitoringTrend> {
  const [month, year] = await Promise.all([
    getMonitoringStatistics("month"),
    getMonitoringStatistics("year"),
  ]);
  return {
    month: month.ok ? month.value : [],
    year: year.ok ? year.value : [],
  };
}

export type LastAlert =
  | { period: "exact"; at: string }
  | { period: "day"; date: string }
  | { period: "month"; month: string };

/** Fallback when statistics/ has no last_alert_at: the newest trend bucket with matches. */
export function lastAlertFromTrend(trend: MonitoringTrend): LastAlert | null {
  const newest = (pts: MonitoringStatPoint[]) =>
    [...pts].reverse().find((p) => p.count > 0 && p.label)?.label ?? null;
  const day = newest(trend.month);
  if (day) return { period: "day", date: day };
  const month = newest(trend.year);
  if (month) return { period: "month", month };
  return null;
}
